import type {ListScheduleItemsResponse} from '@wix/ambassador-events-schedule-v1-schedule-item/types'
import type {V1RichContent} from '@wix/ambassador-events-v1-rich-content/types'
import type {SeatingPlan} from '@wix/ambassador-seating-v1-seating-plan/types'
import type {Group} from '@wix/ambassador-social-groups-v2-group/types'
import type {ClientStatusPublic} from '@wix/cashier-common/dist/src/types/PaymentStatus'
import type {RsvpStatus} from '@wix/events-types'
import type {TranslatedPricingDetails} from '@wix/pricing-plans-tpa-api'
import type {PlaceWithTicketInfo, PlanList, TicketWithFormattedSalePeriod} from '@wix/wix-events-commons-statics'
import type {ControllerFlowAPI, IPlatformAPI, IUser} from '@wix/yoshi-flow-editor'
import type {setFormFactor} from '../../../../commons/actions/environment'
import type {setActiveElement} from '../../../../commons/actions/focus-handler'
import type {DETAILS_ROUTE} from '../../../../commons/constants/navigation'
import type {ReservationState} from '../../../../commons/enums'
import type {FocusHandlerState} from '../../../../commons/reducers/focus-handler'
import type {CommonState, CommonStoreExtraArgs, DemoEvents} from '../../../../commons/types/state'
import type {PageComponentSettings} from '../../stylesParams'
import type {addToCalendarClicked, addToCalendarOptionClicked} from '../actions/calendar'
import type {
  checkout,
  clearCheckout,
  editStep,
  getDiscount,
  handleNextStep,
  nextFormClicked,
  setExpandedTicketIndex,
  setTicketDetails,
  setUseBuyerDetails,
  setValidPaymentAdded,
  submitCheckoutStep,
} from '../actions/checkout'
import type {updateStyleParams} from '../actions/component'
import type {applyCoupon, removeCoupon, resetCouponCode} from '../actions/coupon'
import type {getMembers, shareEvent} from '../actions/event'
import type {joinGroup} from '../actions/groups'
import type {clearInvoice} from '../actions/invoice'
import type {detailsPageLoaded, formPageLoaded, thankYouPageLoaded, ticketedThankYouPageLoaded} from '../actions/loaded'
import type {ensureLoginForMembersOnly, openMembersModal, promptLogin} from '../actions/members'
import type {
  closeNativeModal,
  openCantCompletePaymentModal,
  openCheckoutUnavailable,
  openConfirmRsvp,
  openHeadlessErrorModal,
  openRecurringEventsModal,
  openSeatingPlan,
  openTicketsDetailsModal,
  openTicketsDownloadModal,
  openTimeExpiredModal,
} from '../actions/modals'
import type {
  navigate,
  navigateTo,
  navigateToChangeRsvp,
  navigateToDetails,
  navigateToForm,
  navigateToHomePage,
  navigateToMainPage,
  navigateToOrder,
  navigateToTicketsPicker,
  navigateViaEditor,
  scrollToElement,
} from '../actions/navigation'
import type {downloadTickets} from '../actions/order-success'
import type {applyPlan, openMembershipPicker} from '../actions/paid-plans'
import type {paymentMethodSelected, placeOrderButtonClicked} from '../actions/payment'
import type {
  expressCheckout,
  getOrderTicketsDownloadUrl,
  placeOrder,
  updateOrder,
  updatePaymentMethod,
} from '../actions/placed-order'
import type {openPolicyModal, toggleAgreePolicies} from '../actions/policies'
import type {registrationButtonClicked} from '../actions/registration'
import type {cancelReservation, clearReservationError, reserveTickets} from '../actions/reservation'
import type {
  changeMemberRsvp,
  changeResponse,
  deleteRsvp,
  editRsvpStep,
  handleNextRsvpStep,
  handleRSVP,
  resetRsvpError,
  sendRsvp,
  submitRsvpStep,
  updateRsvp,
  updateRsvpStatus,
} from '../actions/rsvp'
import type {navigateToSchedulePage} from '../actions/schedule'
import type {seatingMapButtonClick} from '../actions/seating/bi'
import type {seatingCheckout} from '../actions/seating/checkout'
import type {
  addPlaceDonation,
  clickPlace,
  resetAreaCounterValue,
  selectPlacePricingOption,
  setAreaCounterValue,
  setPlaceQuantity,
} from '../actions/seating/place'
import type {changeTicketDonation, selectTicket, setDonationError} from '../actions/selected-tickets'
import type {updateSettings} from '../actions/settings'
import type {getStaticMapUrl} from '../actions/static-map'
import type {collapseDescription, expandDescription} from '../actions/tickets-picker'
import type {NativeModal} from '../components/modals/constants'
import type {FormStep, RESERVATION_ERROR, SEATING_ERROR} from '../constants/constants'
import type {setPlaceDonationError, setSeatingError} from '../reducers/seating/errors'
import type {resetFilters, selectLocation, selectPrice} from '../reducers/seating/filters'
import type {
  selectPlace,
  setShowAccessibilityMode,
  setShowMobileBasket,
  setShowPlaceDescription,
  setShowPricingOptionsPlace,
  unselectPlace,
} from '../reducers/seating/mode'
import type {updatePlace} from '../reducers/seating/places'
import type {Api} from '../utils/api'
import type {RsvpErrorMetadata} from './errors'
import type {RegFormData} from './registration'
import type {ScheduleItem} from './schedule'

export interface Actions {
  navigateToForm: typeof navigateToForm
  navigateToMainPage: typeof navigateToMainPage
  navigateToHomePage: typeof navigateToHomePage
  detailsPageLoaded: typeof detailsPageLoaded
  formPageLoaded: typeof formPageLoaded
  thankYouPageLoaded: typeof thankYouPageLoaded
  ticketedThankYouPageLoaded: typeof ticketedThankYouPageLoaded
  changeMemberRsvp: typeof changeMemberRsvp
  registrationButtonClicked: typeof registrationButtonClicked
  updateStyleParams: typeof updateStyleParams
  resetCouponCode: typeof resetCouponCode
  removeCoupon: typeof removeCoupon
  resetRsvpError: typeof resetRsvpError
  sendRsvp: typeof sendRsvp
  updateRsvp: typeof updateRsvp
  reserveTickets: typeof reserveTickets
  selectTicket: typeof selectTicket
  ensureLoginForMembersOnly: typeof ensureLoginForMembersOnly
  expandDescription: typeof expandDescription
  collapseDescription: typeof collapseDescription
  getMembers: typeof getMembers
  promptLogin: typeof promptLogin
  openMembershipPicker: typeof openMembershipPicker
  navigateToTicketsPicker: typeof navigateToTicketsPicker
  openCheckoutUnavailable: typeof openCheckoutUnavailable
  placeOrder: typeof placeOrder
  updateOrder: typeof updateOrder
  paymentMethodSelected: typeof paymentMethodSelected
  placeOrderButtonClicked: typeof placeOrderButtonClicked
  navigateToDetails: typeof navigateToDetails
  nextFormClicked: typeof nextFormClicked
  cancelReservation: typeof cancelReservation
  openHeadlessErrorModal: typeof openHeadlessErrorModal
  openTicketsDetailsModal: typeof openTicketsDetailsModal
  openTicketsDownloadModal: typeof openTicketsDownloadModal
  applyCoupon: typeof applyCoupon
  applyPlan: typeof applyPlan
  handleRSVP: typeof handleRSVP
  checkout: typeof checkout
  submitCheckoutStep: typeof submitCheckoutStep
  editStep: typeof editStep
  openTimeExpiredModal: typeof openTimeExpiredModal
  openCantCompletePaymentModal: typeof openCantCompletePaymentModal
  setValidPaymentAdded: typeof setValidPaymentAdded
  navigate: typeof navigate
  navigateViaEditor: typeof navigateViaEditor
  setUseBuyerDetails: typeof setUseBuyerDetails
  setTicketDetails: typeof setTicketDetails
  clearInvoice: typeof clearInvoice
  navigateToOrder: typeof navigateToOrder
  navigateToChangeRsvp: typeof navigateToChangeRsvp
  setExpandedTicketIndex: typeof setExpandedTicketIndex
  handleNextStep: typeof handleNextStep
  openMembersModal: typeof openMembersModal
  updateSettings: typeof updateSettings
  clearCheckout: typeof clearCheckout
  getDiscount: typeof getDiscount
  downloadTickets: typeof downloadTickets
  shareEvent: typeof shareEvent
  toggleAgreePolicies: typeof toggleAgreePolicies
  openPolicyModal: typeof openPolicyModal
  editRsvpStep: typeof editRsvpStep
  submitRsvpStep: typeof submitRsvpStep
  handleNextRsvpStep: typeof handleNextRsvpStep
  changeResponse: typeof changeResponse
  setFormFactor: typeof setFormFactor
  closeNativeModal: typeof closeNativeModal
  navigateToSchedulePage: typeof navigateToSchedulePage
  addToCalendarClicked: typeof addToCalendarClicked
  addToCalendarOptionClicked: typeof addToCalendarOptionClicked
  openRecurringEventsModal: typeof openRecurringEventsModal
  joinGroup: typeof joinGroup
  openSeatingPlan: typeof openSeatingPlan
  openConfirmRsvp: typeof openConfirmRsvp
  setActiveElement: typeof setActiveElement
  selectPrice: typeof selectPrice
  selectLocation: typeof selectLocation
  setShowAccessibilityMode: typeof setShowAccessibilityMode
  setPlaceQuantity: typeof setPlaceQuantity
  selectPlace: typeof selectPlace
  unselectPlace: typeof unselectPlace
  setSeatingError: typeof setSeatingError
  resetFilters: typeof resetFilters
  seatingMapButtonClick: typeof seatingMapButtonClick
  clearReservationError: typeof clearReservationError
  changeTicketDonation: typeof changeTicketDonation
  setDonationError: typeof setDonationError
  addPlaceDonation: typeof addPlaceDonation
  setPlaceDonationError: typeof setPlaceDonationError
  setShowPricingOptionsPlace: typeof setShowPricingOptionsPlace
  updatePlace: typeof updatePlace
  setShowMobileBasket: typeof setShowMobileBasket
  setShowPlaceDescription: typeof setShowPlaceDescription
  clickPlace: typeof clickPlace
  seatingCheckout: typeof seatingCheckout
  selectPlacePricingOption: typeof selectPlacePricingOption
  updateRsvpStatus: typeof updateRsvpStatus
  deleteRsvp: typeof deleteRsvp
  getOrderTicketsDownloadUrl: typeof getOrderTicketsDownloadUrl
  getStaticMapUrl: typeof getStaticMapUrl
  navigateTo: typeof navigateTo
  expressCheckout: typeof expressCheckout
  scrollToElement: typeof scrollToElement
  updatePaymentMethod: typeof updatePaymentMethod
  setAreaCounterValue: typeof setAreaCounterValue
  resetAreaCounterValue: typeof resetAreaCounterValue
}

export interface State extends CommonState {
  event: EventState
  demoEvents: DemoEvents
  navigation: Navigation
  component: DetailsPageComponent
  placedOrder: PlacedOrderState
  currentMemberDetails: CurrentMemberDetails
  memberRsvp: MemberRsvp
  reservation: Reservation
  checkoutOptions: CheckoutOptionsState
  selectedTickets: SelectedTickets
  pendingRequests: PendingRequests
  tickets: TicketWithFormattedSalePeriod[]
  demoTickets: TicketWithFormattedSalePeriod[]
  ticketsPicker: TicketsPicker
  paidPlans: PaidPlansState
  checkout: CheckoutState
  rsvp: RsvpState
  invoice: InvoiceState
  calendarLinks: wix.events.CalendarLinks
  guest: wix.events.rsvp.Rsvp
  policies: PoliciesState
  modals: ModalsState<NativeModal>
  schedule: ScheduleState
  currentUser: Partial<IUser>
  upcomingOccurrencesCount: number
  groups: GroupsState
  seating: SeatingState
  focusHandler: FocusHandlerState
  formSubmitErrors: FormSubmitErrors
  staticMapUrl: string
}

export interface EventState {
  event: ExtendedEvent
  richContent?: V1RichContent
}

export interface EventsMembersPageState {
  name: string
}

export interface PlacesState {
  places: PlaceWithTicketInfo[]
  areaCounterValues: Record<string, number>
}

export interface SeatingState {
  plan: SeatingPlan
  places: PlacesState
  loading: boolean
  errors: SeatingErrors
  mode: SeatingMode
  filters: SeatingFilters
}

export interface SeatingMode {
  accessibility: boolean
  mobileBasket: boolean
  selectedPlaceId: string
  /* purposefully showDescription and showDescriptionPlace are separate - need to keep history of previously shown place for animations */
  showDescription: boolean
  descriptionPlace: PlaceWithTicketInfo
  showPricingOptionsPlace: PlaceWithTicketInfo
  lastKnownOrigin: string
}

export interface SeatingFilters {
  selectedPrice: string
  selectedZone: string
}

export interface SeatingErrors {
  error: SEATING_ERROR
  planPlaceId: string
  donationErrors: Record<string, DONATION_ERROR>
}

export interface ScheduleState extends ListScheduleItemsResponse {
  items: ScheduleItem[]
}

export type ModalsState<ModalType extends NativeModal> = {
  modalType: ModalType
  payload?: ModalsPayload<ModalType>
}

export type ModalsPayload<ModalType> = ModalType extends NativeModal.HEADLESS_ERROR ? {message: string} : never

export interface PoliciesState {
  agreed: boolean
  policies: wix.events.Policy[]
  showAlert: boolean
}

export interface OrderedTicket extends TicketWithFormattedSalePeriod {
  orderedCount: number
  priceOverride: string
  pricingOptionId?: string
  seats?: SeatsInfo
}

export interface InvoiceState {
  error: string | null
  invoice: wix.events.ticketing.Invoice
  coupon: InvoiceCouponState
}

interface InvoiceCouponState {
  code: string
  ticketId: string
  submitted: boolean
  validating: boolean
}

export interface CheckoutState {
  useBuyerDetails: boolean
  currentStep: FormStep
  validPaymentAdded: boolean
  buyerDetails: RegFormData
  ticketsDetails: RegFormData[]
  ticketsDetailsValidity: boolean[]
  expandedTicketIndex: number
}

export interface FormSubmitErrors {
  error: RsvpErrorMetadata
  errorTime: Date
  canRetry: boolean
}

export interface RsvpState {
  currentStep: FormStep
  rsvpDetails: RegFormData
  response: RsvpStatus
}

export interface PlacedOrderState {
  order: wix.events.ticketing.Order
  orderPageUrl?: string
  loadingTicketsUrl?: boolean
  downloadTicketsUrl?: string
  chargeToken?: string
  clientStatus?: ClientStatusPublic
  pollOrderEnded?: boolean
}

export interface PaidPlansState {
  planList: PlanList
  purchasedPlanList: PlanList
  selectedPaidPlanOrderId: string
  translatedDetails: Record<string, TranslatedPricingDetails>
}

export interface Reservation {
  data: ReservationData
  error: RESERVATION_ERROR
}

interface ReservationData {
  id: string
  expires: string
}

export type GetState = () => State

export interface StoreExtraArgs extends CommonStoreExtraArgs {
  serverApi: Api
  platformAPIs: IPlatformAPI
  flowAPI: ControllerFlowAPI
}

export interface Navigation extends RouteParams {
  sectionPath: string
  slug: string
  route: DETAILS_ROUTE
  query?: Record<string, string>
  shouldNavigateBack: boolean
}

export interface RouteParams {
  reservationId?: string
  reservationState?: ReservationState
  thankYouMessageState?: ThankYouMessageState
  conferencingState?: ConferencingState
}

export type ConferencingState = 'disabled' | 'provider-error'

export type ThankYouMessageState = 'yes' | 'no' | 'waiting'

export interface DetailsPageComponent {
  id: string
  settings: PageComponentSettings
}

export interface CurrentMemberDetails {
  firstName: string
  lastName: string
  email: string
  id: string
}

export interface MemberRsvp {
  rsvp: wix.events.rsvp.Rsvp
  loaded: boolean
}

export interface CheckoutOptionsState {
  loaded: boolean
  options: wix.events.ticketing.GetCheckoutOptionsResponse
}

export interface SelectedTickets {
  [id: string]: SelectedTicket
}

export interface SelectedTicket {
  quantity: number
  donation?: string
  donationError?: DONATION_ERROR
  pricingOptionIds?: string[]
}

export enum DONATION_ERROR {
  EMPTY_DONATION = 'EMPTY_DONATION',
  MINIMUM_NOT_REACHED = 'MINIMUM_NOT_REACHED',
}

export interface PendingRequests {
  requestNames: string[]
}

export interface TicketsPicker {
  tickets: {[ticketDefId: string]: TicketsPickerTicketInfo}
}

interface TicketsPickerTicketInfo {
  expandedDescription?: boolean
}

export interface GroupsState {
  group: Group
  updates: number
}

export interface SeatsInfo {
  placeIds: string[]
  donations: string[]
  pricingOptionIds: string[]
}

export interface TicketsToPlaces {
  [id: string]: SeatsInfo
}

export interface SelectedTicketInfo extends wix.events.ticketing.TicketDefinition {
  place?: PlaceWithTicketInfo
  pricingOptionId?: string
}
